import type { ExploreCourseData } from '@/app/types/interfaces';
import { fuzzyMatchScore, partialMatch } from '@/app/lib/utils';
import { isRecitationMode } from '@/app/lib/meetingTypes';

export type ExploreMatchKind = 'all' | 'direct' | 'fuzzy';

export type ExploreCourseMatch = {
  course: ExploreCourseData;
  kind: ExploreMatchKind;
  relevance: number;
};

function normalize(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function searchableCourseFields(course: ExploreCourseData) {
  return [
    course.baseId,
    course.department,
    course.class_number,
    course.courseName,
    course.fullTitle,
    ...(course.instructors || []),
    ...(course.meetingTypes || []),
  ].map(normalize).filter(Boolean);
}

export function classifyExploreCourseMatch(
  course: ExploreCourseData,
  rawQuery: string,
): ExploreCourseMatch | null {
  const query = normalize(rawQuery);
  if (!query) return { course, kind: 'all', relevance: 0 };

  if (/^(rec|recit|recitation)$/.test(query)) {
    return course.meetingTypes?.some(isRecitationMode)
      ? { course, kind: 'direct', relevance: 1_000 }
      : null;
  }

  const title = normalize(course.fullTitle);
  const shortTitle = normalize(course.courseName);
  const compactQuery = query.replace(/\D/g, '');
  const looksLikeCourseCode = query.replace(/[0-9:\s-]/g, '').length === 0;
  const courseCode = course.baseId.replace(/\D/g, '');
  const exactCodeMatch = looksLikeCourseCode && (
    compactQuery === courseCode ||
    (compactQuery.length === 6 && courseCode.length === 8 && compactQuery === courseCode.slice(2))
  );

  if (title === query || shortTitle === query || exactCodeMatch) {
    return { course, kind: 'direct', relevance: 1_000 };
  }

  if (title.startsWith(query) || shortTitle.startsWith(query)) {
    return { course, kind: 'direct', relevance: 900 };
  }

  if (title.includes(query) || shortTitle.includes(query)) {
    return { course, kind: 'direct', relevance: 800 };
  }

  const searchFields = searchableCourseFields(course);
  const coreMatch = course.coreRequirements.some((entry) => normalize(entry).includes(query));
  if (searchFields.some((field) => field.includes(query)) || coreMatch) {
    return { course, kind: 'direct', relevance: 700 };
  }

  // Identifiers are not prose: a near numeric match can point to an unrelated course.
  if (query.length <= 2 || looksLikeCourseCode) return null;

  const fuzzyTargets = query.includes(' ')
    ? searchFields.filter((field) => partialMatch(query, field))
    : searchFields;
  const fuzzyScore = fuzzyMatchScore(query, fuzzyTargets, { threshold: 0.6 });
  const titlePartiallyMatches = partialMatch(query, course.fullTitle);
  if (fuzzyScore > 0 || titlePartiallyMatches) {
    return {
      course,
      kind: 'fuzzy',
      relevance: Math.round(Math.max(fuzzyScore, titlePartiallyMatches ? 0.6 : 0) * 1_000),
    };
  }

  return null;
}
